/**
 * StructuredDataPanel — the Structured Data tab on the Document Detail page.
 *
 * Extracted from `StructuredDataDemo.jsx` (the public /structured-data-demo
 * route). The layout, drawing, popover, and geometry helpers are identical;
 * only the data source and persistence layer differ:
 *
 *  • Reads real document data from props (`doc.structured_json`, `doc.field_meta`,
 *    `doc.pages[].image`) instead of hardcoded demo data.
 *  • Renders the actual page image via <image href={page.image}/> instead of the
 *    inline SVG mock.
 *  • Saves user-added fields via documentsAPI.addCustomField (POST), updates via
 *    updateCustomField (PATCH), and removes via deleteCustomField (DELETE).
 *  • PaddleOCR auto-suggest (Phase 2) wires in by populating the empty
 *    `regionsByPage` map below — every other code path is already in place.
 *
 * See: structured_data_field_extraction.md (project root).
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2,
  Search, Copy, Pencil, X, Trash2, FileWarning, Loader2,
} from 'lucide-react';
import { documentsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { getImageUrl } from '../../components/ui/PageThumbnail';

// =============================================================================
// Pure helpers — identical to the demo. Geometry only; no React, no API.
// =============================================================================

function toSnakeCase(s) {
  return (s || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Convert a raw field label (typically SNAKE_CASE e.g. "CAPITAL_CASH_AR")
 * into a human-readable display form for the right panel.
 *   "CAPITAL_CASH_AR"  -> { name: "Capital Cash", lang: "AR" }
 *   "BUSINESS_SECTORS" -> { name: "Business Sectors", lang: null }
 *   "EMAIL"            -> { name: "Email", lang: null }
 *   "head_quarter_en"  -> { name: "Head Quarter", lang: "EN" }
 *
 * The lang suffix is split off so it can render as a compact badge next to
 * the field name, instead of feeling like part of the name itself.
 */
function formatFieldLabel(raw) {
  if (!raw) return { name: '', lang: null };
  // Normalise to underscore-separated tokens regardless of case in input
  const tokens = String(raw).split(/[_\s]+/).filter(Boolean);
  if (tokens.length === 0) return { name: '', lang: null };
  // Pull off trailing AR/EN as a language flag — case-insensitive
  let lang = null;
  const last = tokens[tokens.length - 1].toUpperCase();
  if (last === 'AR' || last === 'EN') {
    lang = last;
    tokens.pop();
  }
  const name = tokens
    .map((t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase())
    .join(' ');
  return { name: name || String(raw), lang };
}

function rectsOverlap(a, b) {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

function overlapArea(a, b) {
  const xOverlap = Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x));
  const yOverlap = Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y));
  return xOverlap * yOverlap;
}

/** Find the nearest label region sitting left/above the user's box. */
function findNearestLabel(regions, userBox) {
  if (!Array.isArray(regions) || regions.length === 0) return null;
  const boxCenterY = userBox.y + userBox.h / 2;
  const boxLeft = userBox.x;

  let best = null;
  let bestScore = Infinity;
  for (const r of regions) {
    const b = r.bbox;
    const lblCenterY = b.y + b.h / 2;
    const lblRight = b.x + b.w;
    if (lblRight > boxLeft + 30) continue;
    const yDiff = Math.abs(lblCenterY - boxCenterY);
    if (yDiff > 40) continue;
    const xGap = boxLeft - lblRight;
    if (xGap > 500) continue;
    const score = xGap + yDiff * 3;
    if (score < bestScore) {
      bestScore = score;
      best = r;
    }
  }
  return best;
}

/** Return any text regions whose bbox meaningfully overlaps the user's box. */
function findIntersectingValueText(regions, userBox) {
  if (!Array.isArray(regions) || regions.length === 0) return '';
  const matches = regions.filter((r) => {
    if (!rectsOverlap(r.bbox, userBox)) return false;
    const ra = r.bbox.w * r.bbox.h;
    return overlapArea(r.bbox, userBox) / Math.max(1, ra) >= 0.3;
  });
  if (matches.length === 0) return '';
  matches.sort((a, b) => a.bbox.y - b.bbox.y || a.bbox.x - b.bbox.x);
  return matches.map((m) => m.text).join(' ').trim();
}

// =============================================================================
// Field grouping — convert flat structured_json + field_meta into a flat array
// of field objects the panel can render.
// =============================================================================

function buildFieldList(structuredJson, fieldMeta, documentTypeFields) {
  if (!structuredJson || typeof structuredJson !== 'object') return [];

  // Build a normalized lookup of user-defined fields (name → field def).
  // When non-empty, we filter the display to ONLY these fields — the LLM may
  // extract more than the user asked for (esp. from rich QR-fetched HTML),
  // and the contract is "store all in backend, show only asked on UI".
  const userFieldsByNorm = {};
  if (Array.isArray(documentTypeFields)) {
    documentTypeFields.forEach((f) => {
      if (!f || !f.name) return;
      const k = toSnakeCase(f.name);
      if (k) userFieldsByNorm[k] = f;
    });
  }
  const filterToUserFields = Object.keys(userFieldsByNorm).length > 0;

  // First pass: direct key matches from structured_json
  const matchedUserNorms = new Set();
  const result = Object.entries(structuredJson).reduce((acc, [name, rawValue]) => {
    const norm = toSnakeCase(name);

    // Skip extras when the user has a defined schema
    if (filterToUserFields && !(norm in userFieldsByNorm)) return acc;

    const userField = userFieldsByNorm[norm];
    const meta = (fieldMeta && fieldMeta[name]) || {};
    const isArray = Array.isArray(rawValue);
    const isObject = rawValue !== null && typeof rawValue === 'object' && !isArray;

    // Prefer user-defined datatype; map list/array to "table" for rendering.
    let datatype;
    if (userField?.datatype) {
      const ud = String(userField.datatype).toLowerCase();
      datatype = (ud === 'list' || ud === 'array' || ud === 'table') ? 'table' : ud;
    } else {
      datatype = meta.datatype || (isArray ? 'table' : isObject ? 'object' : 'string');
    }

    let displayValue;
    if (isArray) {
      displayValue = `${rawValue.length} ${rawValue.length === 1 ? 'row' : 'rows'}`;
    } else if (isObject) {
      displayValue = `${Object.keys(rawValue).length} keys`;
    } else {
      displayValue = rawValue == null ? '' : String(rawValue);
    }

    if (userField) matchedUserNorms.add(norm);

    acc.push({
      id: name,
      label: userField?.name || name,
      rawValue,
      value: displayValue,
      page: typeof meta.page === 'number' ? meta.page : null,
      bbox: meta.bbox || null,
      labelBbox: meta.label_bbox || null,
      datatype,
      source: meta.source || 'ai',
      confidence: meta.confidence ?? null,
      isTable: datatype === 'table' || isArray,
    });
    return acc;
  }, []);

  // Second pass: aggregate _ar/_en variants under user-defined field names.
  // The LLM sometimes splits a single user-defined string field (e.g.
  // "registration_name") into language variants ("registration_name_ar" and
  // "registration_name_en"). When the user's defined field is unmatched, see
  // if its _ar/_en variants exist and synthesize a combined display row.
  if (filterToUserFields) {
    const jsonKeys = Object.keys(structuredJson);
    const findKey = (target) => jsonKeys.find((k) => toSnakeCase(k) === target);

    Object.entries(userFieldsByNorm).forEach(([userNorm, userField]) => {
      if (matchedUserNorms.has(userNorm)) return;

      const arKey = findKey(`${userNorm}_ar`);
      const enKey = findKey(`${userNorm}_en`);
      if (!arKey && !enKey) return;

      const arValue = arKey ? structuredJson[arKey] : null;
      const enValue = enKey ? structuredJson[enKey] : null;
      // Display: "English / Arabic" if both, else whichever exists
      const parts = [enValue, arValue]
        .filter((v) => v !== null && v !== undefined && v !== '')
        .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)));
      const displayValue = parts.join(' / ');

      // Borrow bbox/page from whichever variant has one (prefer EN)
      const enMeta = (enKey && fieldMeta && fieldMeta[enKey]) || {};
      const arMeta = (arKey && fieldMeta && fieldMeta[arKey]) || {};
      const meta = enMeta.bbox ? enMeta : (arMeta.bbox ? arMeta : enMeta);

      const userDatatype = String(userField.datatype || 'string').toLowerCase();
      const datatype = (userDatatype === 'list' || userDatatype === 'array' || userDatatype === 'table')
        ? 'table' : userDatatype;

      result.push({
        id: userNorm,
        label: userField.name,
        rawValue: { ar: arValue, en: enValue },
        value: displayValue,
        page: typeof meta.page === 'number' ? meta.page : null,
        bbox: meta.bbox || null,
        labelBbox: meta.label_bbox || null,
        datatype,
        source: meta.source || 'ai',
        confidence: meta.confidence ?? null,
        isTable: false,
      });
    });
  }

  return result;
}

// =============================================================================
// Main component
// =============================================================================

export default function StructuredDataPanel({ doc, onRefetch, readOnly = false }) {
  const pages = useMemo(
    () => (Array.isArray(doc?.pages) ? [...doc.pages].sort((a, b) => a.page_number - b.page_number) : []),
    [doc]
  );
  const totalPages = pages.length;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [search, setSearch] = useState('');
  // Field-list filter chip — one of: 'all' | 'ar' | 'en' | 'tables' | 'empty' | 'custom'
  const [fieldFilter, setFieldFilter] = useState('all');
  const [addFieldMode, setAddFieldMode] = useState(false);
  const [drawing, setDrawing] = useState(null);
  const [pendingField, setPendingField] = useState(null);
  const [savingField, setSavingField] = useState(false);
  // Place-mode: when set to a field id (e.g. "capital_cash_ar"), the next
  // pointer-drawn rectangle is sent to POST /place-field/ for that field
  // instead of opening the new-field popover.
  const [placingFieldId, setPlacingFieldId] = useState(null);
  // Edit-mode: when set, the user is mid-drag on an existing bbox.
  // shape: { fieldId, action: 'move'|'resize-{n,s,e,w,nw,ne,sw,se}',
  //          originBbox, originPointerX, originPointerY, currentBbox }
  const [editingBbox, setEditingBbox] = useState(null);
  // Cache of natural image dimensions per page: { [pageNumber]: { w, h } }
  const [imageDims, setImageDims] = useState({});

  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const fieldRefs = useRef({});

  // ---- Derived data ---------------------------------------------------------

  const allFields = useMemo(
    () => buildFieldList(doc?.structured_json, doc?.field_meta, doc?.document_type_fields),
    [doc?.structured_json, doc?.field_meta, doc?.document_type_fields]
  );

  const selectedField = useMemo(
    () => allFields.find((f) => f.id === selectedFieldId) || null,
    [selectedFieldId, allFields]
  );

  // Fields scoped to current page; fields without a page (legacy AI without
  // recovered positions) are shown in a "Document-level fields" group at top.
  // The matches() helper combines the search query AND the filter chip selection.
  const { documentLevelFields, currentPageFields } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const labelEndsWith = (label, suffix) => {
      if (!label) return false;
      return new RegExp(`(^|[_\\s])${suffix}$`, 'i').test(label);
    };
    const matches = (f) => {
      // Filter chip first — narrows the universe before search
      if (fieldFilter === 'ar' && !labelEndsWith(f.label, 'ar')) return false;
      if (fieldFilter === 'en' && !labelEndsWith(f.label, 'en')) return false;
      if (fieldFilter === 'tables' && !f.isTable) return false;
      if (fieldFilter === 'empty' && f.value != null && String(f.value).trim() !== '') return false;
      // "Custom" covers any user-touched field: brand-new ones drawn via
      // "Add Field" (source='user') AND existing AI fields the user manually
      // placed on the page (source='user_placed').
      if (fieldFilter === 'custom' && f.source !== 'user' && f.source !== 'user_placed') return false;
      // Search query
      if (!q) return true;
      return f.label.toLowerCase().includes(q) || String(f.value).toLowerCase().includes(q);
    };
    return {
      documentLevelFields: allFields.filter((f) => f.page == null && matches(f)),
      currentPageFields: allFields.filter((f) => f.page === currentPage && matches(f)),
    };
  }, [allFields, currentPage, search, fieldFilter]);

  const currentPageData = pages.find((p) => p.page_number === currentPage) || pages[0] || null;
  // imageDims may not be populated yet on first render. We deliberately fall back
  // to a placeholder ONLY for the SVG viewBox (so layout doesn't collapse), but
  // we suppress bbox overlays until real dims are known — otherwise Paddle/pdfplumber
  // bboxes (in image-pixel coords) would render at the wrong scale relative to a
  // viewBox that doesn't yet match the JPEG's natural size.
  const hasRealDims = Boolean(imageDims[currentPage]);
  const currentDims = imageDims[currentPage] || { w: 1240, h: 1754 };

  // Probe natural image dimensions via HTMLImageElement (works for cached and
  // fresh loads, unlike SVG <image>'s unreliable naturalWidth). Triggered when
  // the visible page changes or its image URL changes.
  useEffect(() => {
    if (!currentPageData?.image) return;
    if (imageDims[currentPage]) return;
    const url = getImageUrl(currentPageData.image);
    const probe = new Image();
    let cancelled = false;
    probe.onload = () => {
      if (cancelled) return;
      const nw = probe.naturalWidth || 0;
      const nh = probe.naturalHeight || 0;
      if (nw > 0 && nh > 0) {
        setImageDims((d) => ({ ...d, [currentPage]: { w: nw, h: nh } }));
      }
    };
    probe.onerror = () => {
      // Leave imageDims unset — the viewBox keeps the placeholder.
      // Bboxes will remain hidden via the hasRealDims guard.
    };
    probe.src = url;
    return () => { cancelled = true; };
  }, [currentPage, currentPageData?.image, imageDims]);

  // PaddleOCR regions per page — Phase 2 will populate this from doc.pages[].ocr_regions.
  // In Phase 1 it's always empty, so findNearestLabel / findIntersectingValueText return null/'',
  // making the popover fall back to manual entry (intended behavior for Phase 1).
  const regionsByPage = useMemo(() => {
    const out = {};
    for (const p of pages) {
      out[p.page_number] = Array.isArray(p.ocr_regions) ? p.ocr_regions : [];
    }
    return out;
  }, [pages]);

  // ---- Handlers -------------------------------------------------------------

  const handleFieldClick = (field) => {
    setSelectedFieldId(field.id);
    if (field.page && field.page !== currentPage) setCurrentPage(field.page);
  };

  const handleBoxClick = (field) => {
    if (addFieldMode) return;
    setSelectedFieldId(field.id);
    requestAnimationFrame(() => {
      fieldRefs.current[field.id]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  // Convert client (mouse) point into SVG viewBox coords (= image pixel coords).
  const clientToSvg = useCallback((clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const { x, y } = pt.matrixTransform(ctm.inverse());
    return { x, y };
  }, []);

  const handleDrawPointerDown = useCallback((e) => {
    if (pendingField) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    const { x, y } = clientToSvg(e.clientX, e.clientY);
    setDrawing({ startX: x, startY: y, currentX: x, currentY: y });
  }, [clientToSvg, pendingField]);

  const handleDrawPointerMove = useCallback((e) => {
    const { x, y } = clientToSvg(e.clientX, e.clientY);
    setDrawing((d) => (d ? { ...d, currentX: x, currentY: y } : d));
  }, [clientToSvg]);

  const handleDrawPointerUp = useCallback((e) => {
    if (!drawing) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const x = Math.min(drawing.startX, drawing.currentX);
    const y = Math.min(drawing.startY, drawing.currentY);
    const w = Math.abs(drawing.currentX - drawing.startX);
    const h = Math.abs(drawing.currentY - drawing.startY);
    setDrawing(null);
    if (w < 8 || h < 8) return;

    const bbox = { x, y, w, h };

    // Place-mode: the rectangle is the position for an existing unplaced field.
    // No popover, no name prompt — just POST and refetch.
    // We ALSO sniff out OCR text inside the drawn rect; the backend uses it
    // to auto-populate the field's value if structured_json had no value
    // (common case: LLM left the field null but the value is visible on
    // the page where the user drew).
    if (placingFieldId) {
      const fieldId = placingFieldId;
      const regions = regionsByPage[currentPage] || [];
      const labelHit = findNearestLabel(regions, bbox);
      const detectedValue = findIntersectingValueText(regions, bbox);
      (async () => {
        try {
          await documentsAPI.placeField(doc.id, {
            name: fieldId,
            page: currentPage,
            bbox,
            label_bbox: labelHit?.bbox || null,
            value: detectedValue || null,
          });
          const valueTip = detectedValue ? ` = "${detectedValue.slice(0, 40)}"` : '';
          toast.success(`Placed "${fieldId}"${valueTip} on page ${currentPage}`);
          setPlacingFieldId(null);
          if (typeof onRefetch === 'function') await onRefetch();
        } catch (err) {
          const detail = err?.response?.data?.error || err?.message || 'Failed to place field';
          toast.error(detail);
        }
      })();
      return;
    }

    // Add-field mode: open the popover so the user can name the new field.
    const regions = regionsByPage[currentPage] || [];
    const labelHit = findNearestLabel(regions, bbox);
    const suggestedValue = findIntersectingValueText(regions, bbox);

    setPendingField({
      bbox,
      anchorClientX: e.clientX,
      anchorClientY: e.clientY,
      suggestedName: labelHit ? toSnakeCase(labelHit.text) : '',
      suggestedNameDisplay: labelHit?.text || null,
      suggestedValue: suggestedValue || '',
      detectedLabelBbox: labelHit?.bbox || null,
    });
  }, [drawing, currentPage, regionsByPage, placingFieldId, doc?.id, onRefetch]);

  const handleSaveCustomField = async (label, datatype, value) => {
    if (!pendingField || !doc?.id) return;
    setSavingField(true);
    try {
      await documentsAPI.addCustomField(doc.id, {
        name: label.trim() || 'untitled_field',
        value: value ?? '',
        page: currentPage,
        bbox: pendingField.bbox,
        datatype: datatype || 'string',
        label_bbox: pendingField.detectedLabelBbox || null,
      });
      toast.success('Field added');
      setPendingField(null);
      // Refresh document so the new field shows up in structured_json + field_meta
      if (typeof onRefetch === 'function') await onRefetch();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Failed to save field';
      toast.error(detail);
    } finally {
      setSavingField(false);
    }
  };

  const handleCancelCustomField = () => setPendingField(null);

  const handleDeleteCustomField = async (id) => {
    if (!doc?.id) return;
    if (!window.confirm(`Delete custom field "${id}"?`)) return;
    try {
      await documentsAPI.deleteCustomField(doc.id, id);
      toast.success('Field removed');
      if (selectedFieldId === id) setSelectedFieldId(null);
      if (typeof onRefetch === 'function') await onRefetch();
    } catch (err) {
      const detail = err?.response?.data?.error || err?.message || 'Failed to delete field';
      toast.error(detail);
    }
  };

  // Reset draw state when leaving Add Field mode
  useEffect(() => {
    if (!addFieldMode) {
      setDrawing(null);
      setPendingField(null);
    }
  }, [addFieldMode]);

  // Click "Place" on an unplaced field → enter place mode for that field.
  // We exit Add-Field mode if it was active so the user's pointer events
  // route to place-mode only.
  const handleStartPlacingField = useCallback((fieldId) => {
    setPlacingFieldId(fieldId);
    setAddFieldMode(false);
    setPendingField(null);
    setDrawing(null);
  }, []);

  const handleCancelPlacing = useCallback(() => {
    setPlacingFieldId(null);
    setDrawing(null);
  }, []);

  // ── Bbox edit handlers (move / resize) ─────────────────────────────────
  // These are called from FieldBox handles via props. We track the in-progress
  // bbox in `editingBbox`; on pointerup we POST to /move-field/.
  const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  const computeNewBbox = useCallback((origin, action, dx, dy) => {
    const MIN = 8;
    const W = currentDims?.w || Infinity;
    const H = currentDims?.h || Infinity;
    let x = origin.x, y = origin.y, w = origin.w, h = origin.h;
    if (action === 'move') {
      x = clamp(x + dx, 0, Math.max(0, W - w));
      y = clamp(y + dy, 0, Math.max(0, H - h));
    } else {
      if (action.includes('w')) {
        const nx = clamp(x + dx, 0, x + w - MIN);
        w = w + (x - nx);
        x = nx;
      }
      if (action.includes('e')) {
        w = clamp(w + dx, MIN, W - x);
      }
      if (action.includes('n')) {
        const ny = clamp(y + dy, 0, y + h - MIN);
        h = h + (y - ny);
        y = ny;
      }
      if (action.includes('s')) {
        h = clamp(h + dy, MIN, H - y);
      }
    }
    return { x, y, w, h };
  }, [currentDims]);

  const handleEditPointerDown = useCallback((e, fieldId, action, bbox) => {
    if (addFieldMode || placingFieldId) return;   // don't edit while drawing
    e.stopPropagation();
    e.preventDefault();
    const { x: px, y: py } = clientToSvg(e.clientX, e.clientY);
    setEditingBbox({
      fieldId, action,
      originBbox: { ...bbox },
      originPointerX: px, originPointerY: py,
      currentBbox: { ...bbox },
    });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }, [clientToSvg, addFieldMode, placingFieldId]);

  const handleEditPointerMove = useCallback((e) => {
    if (!editingBbox) return;
    const { x: px, y: py } = clientToSvg(e.clientX, e.clientY);
    const dx = px - editingBbox.originPointerX;
    const dy = py - editingBbox.originPointerY;
    setEditingBbox((s) => s ? ({
      ...s,
      currentBbox: computeNewBbox(s.originBbox, s.action, dx, dy),
    }) : s);
  }, [editingBbox, clientToSvg, computeNewBbox]);

  const handleEditPointerUp = useCallback((e) => {
    if (!editingBbox) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const { fieldId, originBbox, currentBbox } = editingBbox;
    setEditingBbox(null);
    // No movement → no API call (still counts as a click; selection already handled)
    if (originBbox.x === currentBbox.x && originBbox.y === currentBbox.y &&
        originBbox.w === currentBbox.w && originBbox.h === currentBbox.h) {
      return;
    }
    // Detect OCR text under the new bbox to optionally update the value
    const regions = regionsByPage[currentPage] || [];
    const labelHit = findNearestLabel(regions, currentBbox);
    const detectedValue = findIntersectingValueText(regions, currentBbox);
    (async () => {
      try {
        await documentsAPI.moveField(doc.id, {
          name: fieldId,
          page: currentPage,
          bbox: currentBbox,
          label_bbox: labelHit?.bbox || null,
          value: detectedValue || null,
        });
        const tip = detectedValue ? ` → value "${String(detectedValue).slice(0, 40)}"` : '';
        toast.success(`Moved "${fieldId}"${tip}`);
        if (typeof onRefetch === 'function') await onRefetch();
      } catch (err) {
        const detail = err?.response?.data?.error || err?.message || 'Failed to move bbox';
        toast.error(detail);
      }
    })();
  }, [editingBbox, regionsByPage, currentPage, doc?.id, onRefetch]);

  // Keyboard nav — Esc cancels both Add-Field and Place modes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setPendingField(null);
        setDrawing(null);
        setAddFieldMode(false);
        setPlacingFieldId(null);
        setEditingBbox(null);
        return;
      }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight') setCurrentPage((p) => Math.min(totalPages, p + 1));
      if (e.key === 'ArrowLeft') setCurrentPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [totalPages]);

  // ---- Empty states ---------------------------------------------------------

  if (!doc) return null;

  if (totalPages === 0) {
    return (
      <div className="p-8 text-center text-text-muted">
        <FileWarning className="w-8 h-8 mx-auto mb-3 opacity-50" />
        <p className="text-sm">No pages available for this document yet.</p>
      </div>
    );
  }

  if (!doc.structured_json || Object.keys(doc.structured_json).length === 0) {
    return (
      <div className="p-8 text-center text-text-muted">
        <FileWarning className="w-8 h-8 mx-auto mb-3 opacity-50" />
        <p className="text-sm mb-1">No structured data extracted yet.</p>
        <p className="text-xs">Click <span className="text-brand-orange font-medium">Generate Structured Data</span> first.</p>
      </div>
    );
  }

  const pageFieldCount = currentPageFields.length + documentLevelFields.length;

  // ---- Render ---------------------------------------------------------------

  return (
    <div className="flex h-[calc(100vh-260px)] min-h-[600px] bg-dark-900 rounded-lg overflow-hidden border border-dark-600">

      {/* Left rail — page thumbnails */}
      <aside className="w-24 bg-dark-800 border-r border-dark-600 flex flex-col items-center py-3 gap-2 shrink-0 overflow-y-auto">
        {pages.map((p) => (
          <button
            key={p.id}
            onClick={() => setCurrentPage(p.page_number)}
            className={`w-16 group flex flex-col items-center gap-1 transition-all ${
              currentPage === p.page_number ? '' : 'opacity-60 hover:opacity-100'
            }`}
            title={`Page ${p.page_number}`}
          >
            <div className={`w-16 h-22 bg-white rounded overflow-hidden border-2 transition-all ${
              currentPage === p.page_number ? 'border-brand-orange shadow-glow-orange' : 'border-dark-500 group-hover:border-dark-400'
            }`}>
              {p.image
                ? <img src={getImageUrl(p.image)} alt={`Page ${p.page_number}`} className="w-full h-full object-cover object-top" />
                : <div className="w-full h-full bg-dark-700" />}
            </div>
            <span className={`text-[10px] font-medium ${currentPage === p.page_number ? 'text-brand-orange' : 'text-text-muted'}`}>
              {p.page_number}
            </span>
          </button>
        ))}
      </aside>

      {/* Center — document viewport */}
      <main className="flex-1 flex flex-col bg-dark-900 overflow-hidden">

        {/* Toolbar */}
        <div className="h-12 bg-dark-800 border-b border-dark-600 flex items-center justify-between px-4 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-md hover:bg-dark-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm tabular-nums">
              Page <span className="font-semibold text-brand-orange">{currentPage}</span>
              <span className="text-text-muted"> of {totalPages}</span>
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-md hover:bg-dark-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-1">
            {!readOnly && (
              <button
                onClick={() => setAddFieldMode((m) => !m)}
                className={`px-2.5 py-1.5 mr-3 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors border ${
                  addFieldMode
                    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'
                    : 'bg-dark-700 hover:bg-dark-600 text-text-secondary border-dark-500'
                }`}
                title={addFieldMode ? 'Click and drag on the document to draw a box' : 'Toggle add-field mode'}
              >
                <Pencil className="w-3.5 h-3.5" />
                {addFieldMode ? 'Drawing — Esc to exit' : 'Add Field'}
              </button>
            )}
            {/* Zoom controls — min 50%, max 160% (container cap), step 10%.
                Buttons disable at the limits with a muted style so users see they can't go further. */}
            <button
              onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
              disabled={zoom <= 0.5}
              className="p-1.5 rounded-md hover:bg-dark-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label="Zoom out"
              title={zoom <= 0.5 ? 'Minimum zoom reached' : 'Zoom out'}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-text-muted tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
            <button
              onClick={() => setZoom((z) => Math.min(1.6, +(z + 0.1).toFixed(2)))}
              disabled={zoom >= 1.6}
              className="p-1.5 rounded-md hover:bg-dark-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              aria-label="Zoom in"
              title={zoom >= 1.6 ? 'Maximum zoom reached' : 'Zoom in'}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={() => setZoom(1)} className="p-1.5 rounded-md hover:bg-dark-700 transition-colors ml-1" aria-label="Fit to screen" title="Reset to 100%">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Place-mode banner — shown when the user clicked Place on an unplaced field */}
        {placingFieldId && (
          <div className="px-4 py-2 bg-emerald-500/10 border-b border-emerald-500/30 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2 text-emerald-300">
              <span>📍</span>
              <span>
                Placing: <strong className="font-mono">{placingFieldId}</strong>
                <span className="text-text-muted ml-2">— draw a rectangle on the page to set its position.</span>
              </span>
            </div>
            <button
              onClick={handleCancelPlacing}
              className="px-2 py-0.5 rounded text-[10px] font-semibold bg-dark-700 hover:bg-dark-600 text-text-secondary"
              title="Cancel place mode (Esc)"
            >
              Cancel
            </button>
          </div>
        )}

        {/* Document viewport */}
        <div
          ref={viewportRef}
          className="flex-1 overflow-auto flex items-start justify-center p-6 bg-[radial-gradient(ellipse_at_top,_rgba(255,107,53,0.04),_transparent_50%)] relative"
        >
          <div
            style={{ width: `${720 * zoom}px` }}
            className="transition-all duration-200 ease-out"
          >
            <DocumentPageView
              page={currentPageData}
              dims={currentDims}
              // Suppress bbox overlays until the image's natural dimensions are
              // known. Otherwise bboxes (in image-pixel coords) get rendered
              // against a placeholder viewBox and visually misalign.
              fieldsOnPage={hasRealDims ? currentPageFields.filter((f) => f.bbox != null) : []}
              selectedField={hasRealDims ? selectedField : null}
              onBoxClick={handleBoxClick}
              addFieldMode={addFieldMode}
              placingFieldId={placingFieldId}
              drawing={drawing}
              pendingField={pendingField}
              svgRef={svgRef}
              onPointerDown={handleDrawPointerDown}
              onPointerMove={handleDrawPointerMove}
              onPointerUp={handleDrawPointerUp}
              editingBbox={editingBbox}
              onEditPointerDown={readOnly ? undefined : handleEditPointerDown}
              onEditPointerMove={readOnly ? undefined : handleEditPointerMove}
              onEditPointerUp={readOnly ? undefined : handleEditPointerUp}
            />
          </div>

          {pendingField && (
            <NewFieldPopover
              anchorClientX={pendingField.anchorClientX}
              anchorClientY={pendingField.anchorClientY}
              viewportRef={viewportRef}
              suggestedName={pendingField.suggestedName}
              suggestedNameDisplay={pendingField.suggestedNameDisplay}
              suggestedValue={pendingField.suggestedValue}
              saving={savingField}
              onSave={handleSaveCustomField}
              onCancel={handleCancelCustomField}
            />
          )}

          {addFieldMode && !drawing && !pendingField && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-emerald-500/15 text-emerald-300 text-xs font-medium rounded-full border border-emerald-500/30 pointer-events-none flex items-center gap-1.5">
              <Pencil className="w-3 h-3" />
              Click and drag on the document to draw a field box
            </div>
          )}
        </div>
      </main>

      {/* Right panel — fields */}
      <aside className="w-96 bg-dark-800 border-l border-dark-600 flex flex-col shrink-0">
        <div className="px-4 py-3 border-b border-dark-600 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-sm">
              Page <span className="text-brand-orange">{currentPage}</span>
              <span className="text-text-muted/80 font-normal"> · {currentPageFields.length} {currentPageFields.length === 1 ? 'field' : 'fields'} placed</span>
            </h2>
            {documentLevelFields.length > 0 && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded-full"
                title="Fields detected document-wide but not yet positioned on any specific page. Use 'Place' to assign them to a page."
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" />
                {documentLevelFields.length} unplaced
              </span>
            )}
          </div>
          {/* Filter chips — quick narrowing by language / type / state.
              Selected chip uses brand-orange tint; others sit muted. */}
          <div className="flex items-center gap-1.5 mb-2 overflow-x-auto -mx-1 px-1 pb-1 scrollbar-thin">
            {[
              { key: 'all', label: 'All' },
              { key: 'ar', label: 'AR' },
              { key: 'en', label: 'EN' },
              { key: 'tables', label: 'Tables' },
              { key: 'empty', label: 'Empty' },
              { key: 'custom', label: 'Custom' },
            ].map((chip) => {
              const isActive = fieldFilter === chip.key;
              return (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => setFieldFilter(chip.key)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-full border whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-brand-orange/15 text-brand-orange border-brand-orange/40'
                      : 'bg-dark-700/50 text-text-muted border-dark-500 hover:text-text-primary hover:border-dark-400'
                  }`}
                  aria-pressed={isActive}
                >
                  {chip.label}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search fields…`}
              className="w-full pl-9 pr-3 py-2 bg-dark-700 border border-dark-500 rounded-md text-sm placeholder:text-text-muted focus:outline-none focus:border-brand-orange transition-colors"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {pageFieldCount === 0 && (
            <div className="p-8 text-center text-sm text-text-muted">
              {search.trim()
                ? <>No fields match &ldquo;{search}&rdquo;</>
                : <>No fields on this page yet. Use <strong className="text-emerald-400">Add Field</strong> to draw one.</>}
            </div>
          )}

          {/* Page-scoped fields first — the ones with recovered positions, most useful */}
          {currentPageFields.length > 0 && (
            <FieldGroup
              label={`Page ${currentPage} Fields`}
              fields={currentPageFields}
              selectedFieldId={selectedFieldId}
              onFieldClick={handleFieldClick}
              onDeleteCustom={readOnly ? undefined : handleDeleteCustomField}
              fieldRefs={fieldRefs}
            />
          )}

          {/* Below the fold: tables, nulls, and unmatched-text fields — no page yet */}
          {documentLevelFields.length > 0 && (
            <FieldGroup
              label="Unplaced fields"
              fields={documentLevelFields}
              selectedFieldId={selectedFieldId}
              onFieldClick={handleFieldClick}
              onDeleteCustom={readOnly ? undefined : handleDeleteCustomField}
              onPlaceField={readOnly ? undefined : handleStartPlacingField}
              placingFieldId={placingFieldId}
              fieldRefs={fieldRefs}
            />
          )}
        </div>

      </aside>
    </div>
  );
}

// =============================================================================
// FieldGroup — section of the right panel
// =============================================================================

function FieldGroup({ label, fields, selectedFieldId, onFieldClick, onDeleteCustom,
                     onPlaceField, placingFieldId, fieldRefs }) {
  return (
    <div className="border-b border-dark-700 last:border-b-0">
      <div className="px-4 py-2 bg-dark-900/40 sticky top-0 backdrop-blur-sm z-10">
        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</span>
      </div>
      <div className="py-1">
        {fields.map((f) => {
          const isSelected = selectedFieldId === f.id;
          const isCustom = f.source === 'user';
          const isUserPlaced = f.source === 'user_placed';
          // "Place" applies only to unplaced fields (those in the
          // documentLevelFields group — page is null). `onPlaceField` is
          // passed only by that group, so we use its presence as the gate.
          const canPlace = typeof onPlaceField === 'function' && f.page == null;
          const isPlacing = placingFieldId === f.id;
          return (
            <button
              key={f.id}
              ref={(el) => (fieldRefs.current[f.id] = el)}
              onClick={() => onFieldClick(f)}
              className={`w-full text-left px-4 py-2.5 transition-colors flex items-start gap-3 group ${
                isPlacing
                  ? 'bg-emerald-500/10 border-l-2 border-emerald-500'
                  : isSelected
                  ? 'bg-brand-orange/10 border-l-2 border-brand-orange'
                  : 'hover:bg-dark-700/50 border-l-2 border-transparent'
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                  {(() => {
                    const { name, lang } = formatFieldLabel(f.label);
                    return (
                      <>
                        <span className={`text-sm font-medium ${
                          isSelected ? 'text-brand-orange' : 'text-text-primary'
                        }`}>
                          {name}
                        </span>
                        {lang && (
                          <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-dark-700 text-text-muted rounded tracking-wider">
                            {lang}
                          </span>
                        )}
                      </>
                    );
                  })()}
                  {f.isTable && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-status-info/15 text-status-info rounded">table</span>
                  )}
                  {isCustom && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-emerald-500/15 text-emerald-400 rounded" title="Field added by you via Add Field">user</span>
                  )}
                  {isUserPlaced && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-purple-500/15 text-purple-400 rounded" title="AI-extracted field that you positioned on the page">placed</span>
                  )}
                  {isPlacing && (
                    <span className="px-1.5 py-0.5 text-[10px] bg-emerald-500/15 text-emerald-400 rounded animate-pulse">
                      placing…
                    </span>
                  )}
                </div>
                <div className="text-sm text-text-primary truncate font-mono">{f.value}</div>
                {/* Inline preview for list/table fields: show the actual rows
                    as a compact table so users see the block, not just a summary. */}
                {f.isTable && Array.isArray(f.rawValue) && f.rawValue.length > 0
                  && typeof f.rawValue[0] === 'object' && f.rawValue[0] !== null && (
                  <div className="mt-2 overflow-x-auto rounded border border-dark-700 bg-dark-900/30">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="bg-dark-800 text-text-muted">
                          {Object.keys(f.rawValue[0]).map((col) => (
                            <th key={col} className="px-2 py-1 text-left font-semibold border-b border-dark-700">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {f.rawValue.slice(0, 8).map((row, i) => (
                          <tr key={i} className="border-b border-dark-700/50 last:border-b-0">
                            {Object.keys(f.rawValue[0]).map((col) => (
                              <td key={col} className="px-2 py-1 text-text-primary align-top">
                                {row == null ? '' : (typeof row[col] === 'object'
                                  ? JSON.stringify(row[col])
                                  : String(row[col] ?? ''))}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {f.rawValue.length > 8 && (
                      <div className="px-2 py-1 text-[10px] text-text-muted bg-dark-800/50">
                        +{f.rawValue.length - 8} more rows
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-1">
                {canPlace && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlaceField(f.id);
                    }}
                    className={`px-2 py-1 rounded text-[10px] font-semibold flex items-center gap-1 transition-colors ${
                      isPlacing
                        ? 'bg-emerald-500/30 text-emerald-200'
                        : 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
                    }`}
                    aria-label="Draw this field's location on the page"
                    title="Click, then draw a rectangle on the page to set this field's position"
                  >
                    📍 {isPlacing ? 'Placing…' : 'Place'}
                  </button>
                )}
                {isSelected && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard?.writeText(String(f.value));
                      }}
                      className="p-1 rounded hover:bg-dark-600 transition-colors"
                      aria-label="Copy value"
                      title="Copy value"
                    >
                      <Copy className="w-3.5 h-3.5 text-text-muted" />
                    </button>
                    {isCustom && typeof onDeleteCustom === 'function' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCustom(f.id);
                        }}
                        className="p-1 rounded hover:bg-red-500/20 transition-colors"
                        aria-label="Delete custom field"
                        title="Delete this custom field"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// DocumentPageView — the SVG that wraps the page image and overlays
// =============================================================================

function DocumentPageView({
  page, dims, fieldsOnPage = [], selectedField, onBoxClick,
  addFieldMode, placingFieldId, drawing, pendingField, svgRef,
  onPointerDown, onPointerMove, onPointerUp,
  editingBbox, onEditPointerDown, onEditPointerMove, onEditPointerUp,
}) {
  // viewBox uses the page's natural image dimensions — parent passes them in
  // via `dims` once the HTMLImageElement probe completes (see StructuredDataPanel's
  // useEffect). Until then, parent suppresses bbox overlays so they don't render
  // against a placeholder coord space.
  const vbW = dims?.w || 1240;
  const vbH = dims?.h || 1754;
  // Draw mode is active in either Add-Field mode OR Place mode.
  const drawMode = addFieldMode || Boolean(placingFieldId);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${vbW} ${vbH}`}
      className="w-full h-full bg-white rounded-lg shadow-2xl"
      preserveAspectRatio="xMidYMin meet"
      style={{ cursor: drawMode ? 'crosshair' : 'default', touchAction: drawMode ? 'none' : 'auto' }}
      onPointerDown={drawMode ? onPointerDown : undefined}
      onPointerMove={drawMode && drawing ? onPointerMove : undefined}
      onPointerUp={drawMode ? onPointerUp : undefined}
    >
      {/* Page image */}
      {page?.image
        ? (
          <image
            href={getImageUrl(page.image)}
            x="0"
            y="0"
            width={vbW}
            height={vbH}
            preserveAspectRatio="xMidYMin meet"
          />
        )
        : <rect width={vbW} height={vbH} fill="#f1f5f9" />}

      {/* Field overlays */}
      {fieldsOnPage.map((f) => {
        if (!f.bbox) return null;
        const isSelected = selectedField?.id === f.id;
        const color = f.source === 'user' ? '#10b981'
                     : f.source === 'user_placed' ? '#a855f7'
                     : '#3b82f6';
        // Only show the live in-progress bbox preview for the field being dragged
        const editPreview = (editingBbox && editingBbox.fieldId === f.id)
          ? editingBbox.currentBbox : null;
        return (
          <FieldBox
            key={f.id}
            field={f}
            isSelected={isSelected}
            color={color}
            disabled={addFieldMode || Boolean(placingFieldId)}
            onClick={() => !addFieldMode && !placingFieldId && onBoxClick(f)}
            editPreview={editPreview}
            onEditPointerDown={onEditPointerDown}
            onEditPointerMove={onEditPointerMove}
            onEditPointerUp={onEditPointerUp}
          />
        );
      })}

      {/* In-progress draw rectangle */}
      {drawing && (
        <rect
          x={Math.min(drawing.startX, drawing.currentX)}
          y={Math.min(drawing.startY, drawing.currentY)}
          width={Math.abs(drawing.currentX - drawing.startX)}
          height={Math.abs(drawing.currentY - drawing.startY)}
          fill="rgba(16,185,129,0.15)"
          stroke="#10b981"
          strokeWidth="2"
          strokeDasharray="6 4"
          pointerEvents="none"
        />
      )}

      {/* Pending field + auto-detected label */}
      {pendingField && (
        <g pointerEvents="none">
          <rect
            x={pendingField.bbox.x - 2}
            y={pendingField.bbox.y - 2}
            width={pendingField.bbox.w + 4}
            height={pendingField.bbox.h + 4}
            fill="rgba(16,185,129,0.18)"
            stroke="#10b981"
            strokeWidth="2.5"
            rx="3"
          />
          {pendingField.detectedLabelBbox && (
            <>
              <rect
                x={pendingField.detectedLabelBbox.x - 2}
                y={pendingField.detectedLabelBbox.y - 2}
                width={pendingField.detectedLabelBbox.w + 4}
                height={pendingField.detectedLabelBbox.h + 4}
                fill="rgba(59,130,246,0.10)"
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeDasharray="5 3"
                rx="3"
              />
              <line
                x1={pendingField.detectedLabelBbox.x + pendingField.detectedLabelBbox.w + 2}
                y1={pendingField.detectedLabelBbox.y + pendingField.detectedLabelBbox.h / 2}
                x2={pendingField.bbox.x - 2}
                y2={pendingField.bbox.y + pendingField.bbox.h / 2}
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeDasharray="3 3"
              />
            </>
          )}
        </g>
      )}
    </svg>
  );
}

function FieldBox({ field: f, isSelected, color, disabled, onClick,
                   editPreview, onEditPointerDown, onEditPointerMove, onEditPointerUp }) {
  // Live preview takes over the displayed rect while the user drags
  const bb = editPreview || f.bbox;
  const isDragging = Boolean(editPreview);
  // Resize/move only enabled when selected AND not in addField/place mode AND
  // the parent gave us handlers.
  const canEdit = isSelected && !disabled && typeof onEditPointerDown === 'function';

  const startEdit = (action) => (e) => {
    if (!canEdit) return;
    onEditPointerDown(e, f.id, action, f.bbox);
  };

  // 8 handles: 4 corners + 4 edge midpoints
  // Each handle is an SVG rect with its own cursor + pointer handlers.
  const HSIZE = 10;   // handle visual size (image-pixel units)
  const halfH = HSIZE / 2;
  const handles = canEdit ? [
    { id: 'nw', cx: bb.x,             cy: bb.y,             cursor: 'nwse-resize' },
    { id: 'n',  cx: bb.x + bb.w / 2,  cy: bb.y,             cursor: 'ns-resize'   },
    { id: 'ne', cx: bb.x + bb.w,      cy: bb.y,             cursor: 'nesw-resize' },
    { id: 'e',  cx: bb.x + bb.w,      cy: bb.y + bb.h / 2,  cursor: 'ew-resize'   },
    { id: 'se', cx: bb.x + bb.w,      cy: bb.y + bb.h,      cursor: 'nwse-resize' },
    { id: 's',  cx: bb.x + bb.w / 2,  cy: bb.y + bb.h,      cursor: 'ns-resize'   },
    { id: 'sw', cx: bb.x,             cy: bb.y + bb.h,      cursor: 'nesw-resize' },
    { id: 'w',  cx: bb.x,             cy: bb.y + bb.h / 2,  cursor: 'ew-resize'   },
  ] : [];

  return (
    <g
      onClick={onClick}
      onPointerMove={canEdit ? onEditPointerMove : undefined}
      onPointerUp={canEdit ? onEditPointerUp : undefined}
      style={{ cursor: disabled ? 'crosshair' : (canEdit ? 'move' : 'pointer') }}
    >
      {/* Body rect (also the move-grab area when selected) */}
      <rect
        x={bb.x - 4}
        y={bb.y - 4}
        width={bb.w + 8}
        height={bb.h + 8}
        rx="4"
        fill={isDragging ? `${color}55` : (isSelected ? `${color}33` : `${color}10`)}
        stroke={color}
        strokeWidth={isSelected ? 3 : 1.5}
        strokeDasharray={isDragging ? '6 4' : (isSelected ? '0' : '4 4')}
        className={isSelected && !isDragging ? 'animate-pulse-slow' : ''}
        onPointerDown={canEdit ? startEdit('move') : undefined}
        style={{ cursor: canEdit ? 'move' : undefined }}
      />

      {/* 8 resize handles, only when selected */}
      {handles.map((h) => (
        <rect
          key={h.id}
          x={h.cx - halfH}
          y={h.cy - halfH}
          width={HSIZE}
          height={HSIZE}
          rx="2"
          fill="#ffffff"
          stroke={color}
          strokeWidth="2"
          style={{ cursor: h.cursor }}
          onPointerDown={startEdit(`resize-${h.id}`)}
        />
      ))}

      {/* Selected-state label tag above the bbox */}
      {isSelected && (
        <g pointerEvents="none">
          <rect
            x={bb.x - 4}
            y={bb.y - 28}
            width={Math.max(120, f.label.length * 7 + 20)}
            height="22"
            rx="3"
            fill={color}
          />
          <text
            x={bb.x + 6}
            y={bb.y - 12}
            fontSize="12"
            fontWeight="600"
            fill="#ffffff"
            fontFamily="Inter, sans-serif"
          >
            {(() => {
              const { name, lang } = formatFieldLabel(f.label);
              return `${name}${lang ? ` · ${lang}` : ''}${isDragging ? ' · dragging…' : ''}`;
            })()}
          </text>
        </g>
      )}
    </g>
  );
}

// =============================================================================
// NewFieldPopover — appears next to the freshly-drawn box
// =============================================================================

// Matches the datatype list used on the Document Types admin page
// (frontend/src/pages/admin/DocumentTypes.jsx) plus an extra "table" option
// for tabular fields drawn via the bbox flow.
const DATATYPE_OPTIONS = [
  { value: 'string',     label: 'String' },
  { value: 'integer',    label: 'Integer' },
  { value: 'float',      label: 'Float' },
  { value: 'boolean',    label: 'Boolean' },
  { value: 'date',       label: 'Date' },
  { value: 'datetime',   label: 'Datetime' },
  { value: 'email',      label: 'Email' },
  { value: 'phone',      label: 'Phone' },
  { value: 'url',        label: 'URL' },
  { value: 'currency',   label: 'Currency' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'address',    label: 'Address' },
  { value: 'name',       label: 'Name' },
  { value: 'company',    label: 'Company' },
  { value: 'list',       label: 'List' },
  { value: 'table',      label: 'Table' },
];

function NewFieldPopover({
  anchorClientX, anchorClientY, viewportRef,
  suggestedName = '', suggestedNameDisplay = null, suggestedValue = '',
  saving = false,
  onSave, onCancel,
}) {
  const [name, setName] = useState(suggestedName);
  const [datatype, setDatatype] = useState('string');
  const [value, setValue] = useState(suggestedValue);
  const popoverRef = useRef(null);
  const nameInputRef = useRef(null);
  const valueInputRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const vp = viewportRef.current;
    const pop = popoverRef.current;
    if (!vp || !pop) return;
    const vpRect = vp.getBoundingClientRect();
    const popRect = pop.getBoundingClientRect();
    const PAD = 12;
    let left = anchorClientX - vpRect.left + 12;
    let top = anchorClientY - vpRect.top + 12;
    if (left + popRect.width > vpRect.width - PAD) left = vpRect.width - popRect.width - PAD;
    if (top + popRect.height > vpRect.height - PAD) top = vpRect.height - popRect.height - PAD;
    if (left < PAD) left = PAD;
    if (top < PAD) top = PAD;
    setPosition({ top, left });
    if (suggestedName) {
      valueInputRef.current?.focus();
      valueInputRef.current?.select();
    } else {
      nameInputRef.current?.focus();
    }
  }, [anchorClientX, anchorClientY, viewportRef, suggestedName]);

  const canSave = !saving && name.trim().length > 0;

  const submit = (e) => {
    e?.preventDefault?.();
    if (!canSave) return;
    onSave(name, datatype, value);
  };

  return (
    <form
      ref={popoverRef}
      onSubmit={submit}
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute z-30 w-72 bg-dark-800 border border-dark-500 rounded-lg shadow-2xl p-4 animate-scale-in"
      style={{ top: position.top, left: position.left }}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-1.5">
          <Pencil className="w-3.5 h-3.5 text-emerald-400" />
          New Field
        </h3>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 rounded hover:bg-dark-700 transition-colors"
          aria-label="Cancel"
          disabled={saving}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Field name</label>
        {suggestedNameDisplay && (
          <span className="text-[10px] text-blue-400 flex items-center gap-1 font-medium">
            <span className="inline-block w-1.5 h-1.5 bg-blue-400 rounded-full" />
            Detected: {suggestedNameDisplay}
          </span>
        )}
      </div>
      <input
        ref={nameInputRef}
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. trade_license_number"
        disabled={saving}
        className={`w-full px-2.5 py-1.5 mb-3 bg-dark-700 border rounded-md text-sm placeholder:text-text-muted focus:outline-none focus:border-emerald-500 transition-colors ${
          suggestedName && name === suggestedName ? 'border-blue-500/60' : 'border-dark-500'
        }`}
      />

      <label className="block text-[11px] font-semibold uppercase tracking-wider text-text-muted mb-1">Datatype</label>
      <select
        value={datatype}
        onChange={(e) => setDatatype(e.target.value)}
        disabled={saving}
        className="w-full px-2.5 py-1.5 mb-3 bg-dark-700 border border-dark-500 rounded-md text-sm focus:outline-none focus:border-emerald-500 transition-colors"
      >
        {DATATYPE_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <div className="flex items-center justify-between mb-1">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">Value</label>
        {suggestedValue
          ? <span className="text-[10px] text-emerald-400 flex items-center gap-1 font-medium">
              <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full" />
              Auto-filled from OCR
            </span>
          : <span className="text-[10px] text-text-muted/60">manual entry</span>}
      </div>
      <input
        ref={valueInputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Type the value as it appears…"
        disabled={saving}
        className={`w-full px-2.5 py-1.5 mb-3 bg-dark-700 border rounded-md text-sm placeholder:text-text-muted focus:outline-none focus:border-emerald-500 transition-colors font-mono ${
          suggestedValue && value === suggestedValue ? 'border-emerald-500/60' : 'border-dark-500'
        }`}
      />

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-xs rounded-md hover:bg-dark-700 text-text-secondary transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSave}
          className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:bg-dark-600 disabled:text-text-muted disabled:cursor-not-allowed text-white transition-colors flex items-center gap-1.5"
        >
          {saving && <Loader2 className="w-3 h-3 animate-spin" />}
          {saving ? 'Saving…' : 'Save Field'}
        </button>
      </div>
    </form>
  );
}